// src/encounter/previous-notes-section.tsx
// Pre-encounter CENTER-column "Previous Notes" section (PROD-1523). React port of Flutter
// pre_encounter_widget.dart previousNotes() (:893) + NotesRowWidget (:2050): a "PREVIOUS NOTES"
// header (shown for follow-up encounters) + one row per prior COMPLETED encounter — a clinical
// icon · reason-for-visit title (blue) with a diagnosis summary appended (grey) · a
// "{location}  •  {dateTime}" meta line · a "View" button that opens the note.
//
// PRESENTATIONAL: takes the mapped view models (to-previous-notes). HIDDEN when there are no notes
// (Flutter parity: previousNotes() returns Container() when the filtered list is empty). DS atoms +
// tokens only — no hardcoded hex, no invented chrome. The "View" action is raised via onView; when
// omitted, rows render display-only (the host need not wire the note viewer yet).
import { Button, EncounterSectionHeader } from '@scripsteam/scrips-signal-ds';
import { ClipboardList } from 'lucide-react';
import type { PreviousNoteView } from './lib/to-previous-notes';
import { useDesignV2 } from './lib/design-v2';
import { EncBlock, EncSectionCard } from './enc-section-card';

export interface PreviousNotesSectionProps {
  notes: PreviousNoteView[];
  /** Open the selected prior note (Flutter showEncounterDetail). Omitted → rows render display-only
   *  (no "View" button), so the host call-site need not wire the note viewer yet. */
  onView?: (note: PreviousNoteView) => void;
}

const CARD =
  'bg-surface-card border border-border-default ' +
  'rounded-lg p-6';
const ROW =
  'flex items-center gap-3 p-3 ' +
  'border border-border-default rounded-md ' +
  // `--color-surface-subtle` has no DS token today, so the fallback the old inline style leaned
  // on is inlined directly as the surface-card colour.
  'bg-surface-card';
// Title mirrors the vaccine section TITLE token; Flutter renders the RFV in brand blue and appends
// the diagnosis summary in grey as one rich-text line.
const TITLE = 'text-size-sm font-semibold text-text-primary';
const DIAGNOSIS = 'font-medium text-text-hint';
const META = 'mt-1 text-size-xs text-text-hint';
const STACK_2 = 'flex flex-col gap-2';

/** "{location}  •  {dateTime}" — drops whichever part is missing (Flutter shows emptyLabel). */
function metaLine(note: PreviousNoteView): string | null {
  const parts = [note.locationLabel, note.dateTimeLabel].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join('  •  ') : null;
}

export function PreviousNotesSection({ notes, onView }: PreviousNotesSectionProps) {
  const designV2 = useDesignV2();
  // No prior notes → section hidden (Flutter parity: previousNotes() returns an empty Container).
  if (notes.length === 0) return null;

  // Frame v2 (design export 21-10, EncCorePreviousNotes): one Objective card, one block, hairline-separated rows.
  if (designV2) {
    return (
      <EncSectionCard section="o" aria-label="Previous notes">
        <EncBlock title="Previous notes" icon="docs">
          <div data-slot="pv-stack">
            {notes.map((note) => {
              const meta = metaLine(note);
              return (
                <div key={note.id} data-slot="pv-row">
                  <span data-slot="pv-glyph">
                    <ClipboardList className="size-4" aria-hidden />
                  </span>
                  <div data-slot="pv-grow">
                    <div data-slot="pv-title">
                      {note.reasonForVisit || 'Encounter note'}
                      {note.diagnosisSummary && <span data-slot="pv-dx"> {note.diagnosisSummary}</span>}
                    </div>
                    {meta && <p data-slot="pv-meta">{meta}</p>}
                  </div>
                  {onView && (
                    <Button variant="secondary" size="sm" onClick={() => onView(note)}>
                      View
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </EncBlock>
      </EncSectionCard>
    );
  }

  return (
    <section className={CARD} aria-label="Previous notes">
      <EncounterSectionHeader title="Previous Notes" variant="section" />
      <div className="h-3" />
      <div className={STACK_2}>
        {notes.map((note) => {
          const meta = metaLine(note);
          return (
            <div key={note.id} className={ROW}>
              <ClipboardList className="size-4 shrink-0 text-text-secondary" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className={TITLE}>
                  {note.reasonForVisit || 'Encounter note'}
                  {note.diagnosisSummary && <span className={DIAGNOSIS}> {note.diagnosisSummary}</span>}
                </div>
                {meta && <p className={META}>{meta}</p>}
              </div>
              {onView && (
                <Button variant="secondary" size="sm" onClick={() => onView(note)}>
                  View
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
